import { useContext, useEffect, useState } from "react";
import Logo from "../../assets/img/skorLogo.svg";
import useTranslator from "../../hooks/useTranslator";
import jwtDecode from "jwt-decode";
import { Link, useNavigate } from "react-router-dom";
import EmailForm from "../../components/Auth/EmailForm";
import EnterOtpModal from "../onboarding/enterOtpModal";
import BasicInfoModalRaw from "../onboarding/BasicInfoModal";
import basicInfoStyles from "../onboarding/BasicInfoModal.module.css";
import constants from "../../Utils/constants";
import axios from "axios";
import { haveValue, toast } from "../../helper/common";
import TenantContext from "../../context/tenant/TenantContext";
import AuthContext from "../../context/auth/AuthContext";
import { useCookies } from "react-cookie";
import { trackClarityEvent } from "../../Utils/clarityReady";
import OnboardingProgress from "../onboarding/OnboardingProgress";
import AuthSidePanel from "../../components/Auth/AuthSidePanel";
import { getUserRoleFromPath } from "./authFlow";
import { Button, LanguageSwitch } from "../../components/design";
import AuthStepHeader from "../../components/Auth/AuthStepHeader";
import type { AuthMode, AuthScreen, UserLoginForm } from "./authFlow";

const BasicInfoModal: any = BasicInfoModalRaw;

interface TenantAuthProps {
  onChangeHandler: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled: boolean;
  emailLoading: boolean;
  emailSubmitHandler: (event: React.FormEvent<HTMLFormElement>) => void;
  currentScreen: AuthScreen;
  setCurrentScreen: (screen: AuthScreen) => void;
  sendOtpFun: (email?: string) => void;
  otp: string;
  setOtp: (otp: string) => void;
  showResend: boolean;
  showTimer: number;
  currentLang: string;
  handleLanguageChange: (lang: string) => void;
  verifyOtpHandler: (
    event: React.FormEvent<HTMLFormElement>,
    createUser?: () => Promise<void>
  ) => Promise<void>;
  userLogin: UserLoginForm;
  setUserLogin: React.Dispatch<React.SetStateAction<UserLoginForm>>;
  runTimer: () => void;
  companyLogo?: string | null;
  redirectFun: (data: any) => void;
  verifyOtpLoading: boolean;
  from?: string | null;
  landlordsData?: any;
  setMode?: (mode: AuthMode) => void;
  setIsUserVerified?: (v: boolean) => void;
  setUserAlreadyExists?: (v: boolean) => void;
}

interface InvitationJwt {
  exp?: number;
  email?: string;
}

const TenantAuth = ({
  onChangeHandler,
  disabled,
  emailLoading,
  emailSubmitHandler,
  currentScreen,
  setCurrentScreen,
  sendOtpFun,
  otp,
  setOtp,
  showResend,
  showTimer,
  currentLang,
  handleLanguageChange,
  verifyOtpHandler,
  userLogin,
  setUserLogin,
  runTimer,
  companyLogo,
  redirectFun,
  verifyOtpLoading,
  from,
  landlordsData,
  setMode,
  setIsUserVerified,
  setUserAlreadyExists,
}: TenantAuthProps) => {
  const { _t } = useTranslator();
  const { getUserByEmail } = useContext(AuthContext);
  const navigate = useNavigate();
  const [phoneNumber, setPhoneNumber] = useState("");
  const { submitProtectedHousingDocuments } = useContext(TenantContext);
  const [cookies, setCookie, removeCookie] = useCookies<string>([
    "sharedProfileLink",
    "libeenWorkspaceTenant",
    "catellaWorkspaceTenant",
    "deaWorkspaceTenant",
    "invitationToken",
    "invitedEmail",
  ]);
  const [invitationToken, setInvitationToken] = useState<string | null>(null);
  // New signups choose first: tenant (BasicInfo flow) or company (KYB flow).
  const [signupAs, setSignupAs] = useState<"tenant" | "company">("tenant");
  const [signupAsConfirmed, setSignupAsConfirmed] = useState(false);
  const [companySignupLoading, setCompanySignupLoading] = useState(false);
  const [isInvitedEmail, setIsInvitedEmail] = useState(false);

  useEffect(() => {
    const checkInvitation = async () => {
      if (haveValue(cookies?.invitationToken)) {
        try {
          const decoded = jwtDecode<InvitationJwt>(cookies.invitationToken);

          if (decoded.exp && decoded.exp * 1000 < Date.now()) {
            removeCookie("invitationToken", { path: "/", maxAge: 1800 });
            removeCookie("requestProfileLink", { path: "/", maxAge: 1800 });
            navigate("/tenant/auth");
            return;
          }

          if (haveValue(decoded?.email)) {
            setUserLogin((prevData) => ({
              ...prevData,
              email: decoded.email as string,
            }));

            const ifUserAlreadyExists = await getUserByEmail({
              email: decoded.email,
              type: "tenant",
            });

            if (ifUserAlreadyExists?.user) {
              return;
            }

            setInvitationToken(cookies?.invitationToken);
            // new flow: require OTP first even for invited users
            // Ensure parent auth state aligns with normal signup flow
            setMode?.("signup");
            setUserAlreadyExists?.(false);
            setIsUserVerified?.(false);
            setCurrentScreen("verifyOtp");
            sendOtpFun(decoded.email);
            trackClarityEvent("tenant_onboarding_started");
          }
        } catch (err) {
          removeCookie("invitationToken", { path: "/", maxAge: 1800 });
          removeCookie("requestProfileLink", { path: "/", maxAge: 1800 });
          navigate("/tenant/auth");
        }
      }
    };

    checkInvitation();
  }, [cookies?.invitationToken]);

  useEffect(() => {
    const checkInvitedEmail = async () => {
      if (haveValue(cookies?.invitedEmail)) {
        const email = cookies.invitedEmail;
        setUserLogin((prevData) => ({
          ...prevData,
          email,
        }));

        const ifUserAlreadyExists = await getUserByEmail({
          email,
          type: "tenant",
        });

        if (ifUserAlreadyExists?.user) {
          return;
        }

        setIsInvitedEmail(true);
        // Align parent state with normal tenant signup flow
        setMode?.("signup");
        setUserAlreadyExists?.(false);
        setIsUserVerified?.(false);
        setCurrentScreen("verifyOtp");
        sendOtpFun(email);
        trackClarityEvent("tenant_onboarding_started");
      }
    };

    checkInvitedEmail();
  }, [cookies?.invitedEmail]);

  useEffect(() => {
    if (localStorage.getItem("usertype") === "tenant") {
      setUserLogin((prevData) => ({
        ...prevData,
        phone_number: phoneNumber?.replace("+", ""),
      }));
    } else {
      setUserLogin((prevData) => ({ ...prevData, phone: phoneNumber }));
    }
  }, [phoneNumber]);

  const [selectedProtectedHousingDocs, setSelectedProtectedHousingDocs] =
    useState<any[]>([]);

  const requiresSubcategory = (status: string) => {
    return [
      constants.public_servant,
      constants.employed,
      constants.self_employed,
    ].includes(status);
  };

  const submitHandler = async (
    e: React.FormEvent | Record<string, any>,
    verifyOtp?: boolean
  ): Promise<void> => {
    try {
      if (verifyOtp) {
        // OTP step for signup flow - just verify, BasicInfo will call registerUser after
        await verifyOtpHandler(e as React.FormEvent<HTMLFormElement>);
      } else {
        const signupConsents =
          "consents" in e ? e.consents : userLogin?.consents;
        await registerUser(signupConsents);
      }
    } catch (error) {}
  };

  const registerUser = async (
    signupConsents?: Record<string, boolean>,
    tenantType?: string
  ): Promise<void> => {
    try {
      if (isInvitedEmail) {
        removeCookie("invitedEmail", { path: "/" });
      }
      let protected_housing = false;
      if (!selectedProtectedHousingDocs.includes("no")) {
        protected_housing = true;
      }

      const registerData: Record<string, any> = {
        email: userLogin.email,
        password: userLogin.password,
        confirmPassword: userLogin.confirmPassword,
        type: userLogin.type,
        checked: true,
        analysisCompleted: true,
        user_type: userLogin.user_type,
        qrCode_signup: userLogin?.qrCode_signup,
        requestProfileLink: userLogin?.requestProfileLink,
        fname: userLogin?.fname,
        lname: userLogin?.lname,
        dni_nie:
          userLogin?.document_type === "dni_nie"
            ? userLogin?.identity_number
            : null,
        passport_id:
          userLogin?.document_type === "passport"
            ? userLogin?.identity_number
            : null,
        post_code: userLogin?.post_code,
        dob: userLogin?.dob,
        employment_status: userLogin?.employment_status,
        employment_category: userLogin?.employment_category,
        current_annual_income: userLogin?.current_annual_income,
        currency: userLogin?.currency,
        protected_housing: userLogin?.protected_housing,
        phone_number: userLogin?.phone_number,
        lang: currentLang,
        need_password: false,
        skip_steps: userLogin?.skip_steps,
        company_legal_name:
          userLogin?.employment_status === "company"
            ? userLogin?.company_legal_name || null
            : null,
        company_cif:
          userLogin?.employment_status === "company"
            ? userLogin?.company_cif || null
            : null,
        company_type:
          userLogin?.employment_status === "company"
            ? userLogin?.company_type || null
            : null,
        company_cnae:
          userLogin?.employment_status === "company"
            ? userLogin?.company_cnae || null
            : null,
        company_representative_position:
          userLogin?.employment_status === "company"
            ? userLogin?.company_representative_position || null
            : null,
        highest_qualification: userLogin?.highest_qualification || null,
        consents: signupConsents,
        tenant_type: tenantType,
      };
      if (haveValue(invitationToken)) {
        registerData.invitationToken = invitationToken;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/users/signup`,
        registerData,
        { headers: { "Accept-Language": localStorage.getItem("i18nextLng") } }
      );
      const data = response.data;
      if (data) {
        localStorage.setItem("token", data.token);
        localStorage.setItem("saveduserid", data.user._id);

        setUserLogin((prev) => {
          return {
            ...prev,
            _id: data.user._id,
          };
        });

        trackClarityEvent("tenant_basic_info_step_ended");
        setTimeout(() => {
          trackClarityEvent("tenant_kyc_step_started");
        }, constants.CLARITY_DELAY);
      }
      const payload = {
        allDocuments: Array.isArray(selectedProtectedHousingDocs)
          ? selectedProtectedHousingDocs
          : [],
      };
      await submitProtectedHousingDocuments(protected_housing ? payload : []);
      if (cookies?.catellaWorkspaceTenant) {
        removeCookie("catellaWorkspaceTenant", { path: "/", maxAge: 1800 });
      }
      if (cookies?.libeenWorkspaceTenant) {
        removeCookie("libeenWorkspaceTenant", { path: "/", maxAge: 1800 });
      }
      if (cookies?.deaWorkspaceTenant) {
        removeCookie("deaWorkspaceTenant", { path: "/", maxAge: 1800 });
      }

      if (haveValue(invitationToken) && response?.data?.auth === true) {
        removeCookie("invitationToken", { path: "/" });
      }
      if (
        (!haveValue(invitationToken) && !isInvitedEmail) ||
        response?.data?.auth === true
      ) {
        redirectFun(response.data);
      }
    } catch (error: any) {
      const data = error?.response?.data;
      if (data?.status === "error" && data?.msg) {
        toast.error(data?.msg);
      } else if (haveValue(data?.key)) {
        toast.error(_t(data?.key));
      }
    }
  };

  if (currentScreen === "signup") {
    return (
      <div className="tenant-onboarding-shell">
        <OnboardingProgress
          step={1}
          identityStage="document"
          skipFinancialStudy={userLogin?.skip_steps?.includes(
            constants.SKIP_STEPS_ENUM.bank_onboarding
          )}
          companyLogo={undefined}
        />
        <div
          className="tenant-auth-language-switch"
          aria-label="language switch"
        >
          <LanguageSwitch
            currentLang={currentLang}
            onChange={handleLanguageChange}
          />
        </div>
        {!signupAsConfirmed ? (
          <div className={basicInfoStyles.basicInfoRedesign}>
            <div
              className={basicInfoStyles.progressSpacer}
              aria-hidden="true"
            />
            <div className={basicInfoStyles.canvas}>
              <div className={basicInfoStyles.cardShell}>
                <form
                  className={basicInfoStyles.card}
                  onSubmit={(e) => e.preventDefault()}
                >
                  <AuthStepHeader
                    onBack={() => setCurrentScreen("email")}
                    title={_t(
                      "loggingInAs",
                      "Tenant signup, after OTP: question asking whether the user signs up as a tenant or a company"
                    )}
                    subtitle={_t(
                      "loggingInAsDescription",
                      "Tenant signup, after OTP: short help under the tenant/company question; a company verifies the business and adds the people who will live in the property"
                    )}
                  />
                  <div className={basicInfoStyles.formGrid}>
                    <div className="auth-choice-list">
                      {(
                        [
                          {
                            type: "tenant",
                            img: "/images/kyc.svg",
                            title: _t(
                              "signupAsTenant",
                              "Tenant signup, after OTP: option to continue as an individual tenant"
                            ),
                            description: _t(
                              "signupAsTenantDescription",
                              "Tenant signup, after OTP: description under the individual tenant option"
                            ),
                          },
                          {
                            type: "company",
                            img: "/images/building-icon.svg",
                            title: _t(
                              "signupAsCompany",
                              "Tenant signup, after OTP: option to continue as a company (KYB flow)"
                            ),
                            description: _t(
                              "signupAsCompanyDescription",
                              "Tenant signup, after OTP: description under the company option (verify the business, add occupants)"
                            ),
                          },
                        ] as const
                      ).map((item) => (
                        <div
                          key={item.type}
                          className={`user-type-card ${
                            signupAs === item.type ? "selected-card" : ""
                          }`}
                          role="radio"
                          aria-checked={signupAs === item.type}
                          tabIndex={0}
                          onClick={() => setSignupAs(item.type)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              setSignupAs(item.type);
                            }
                          }}
                        >
                          <span className="user-type-icon">
                            <img src={item.img} alt="" />
                          </span>
                          <div className="user-type-card-text">
                            <h2>{item.title}</h2>
                            <p>{item.description}</p>
                          </div>
                          <span
                            className="user-type-radio"
                            aria-hidden="true"
                          />
                        </div>
                      ))}
                    </div>
                    <div className={basicInfoStyles.actions}>
                      <Button
                        size="lg"
                        block
                        loading={companySignupLoading}
                        onClick={async () => {
                          if (signupAs === "tenant") {
                            setSignupAsConfirmed(true);
                            return;
                          }
                          setCompanySignupLoading(true);
                          try {
                            await registerUser(
                              undefined,
                              constants.TENANT_TYPE.COMPANY
                            );
                          } finally {
                            setCompanySignupLoading(false);
                          }
                        }}
                      >
                        {_t("continue")}
                      </Button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        ) : (
          <BasicInfoModal
            backBtn={() => setSignupAsConfirmed(false)}
            formData={userLogin}
            setFormData={setUserLogin}
            createUserProfile={submitHandler}
            requiresSubcategory={requiresSubcategory}
            phoneNumber={phoneNumber}
            setPhoneNumber={setPhoneNumber}
            selectedProtectedHousingDocs={selectedProtectedHousingDocs}
            setSelectedProtectedHousingDocs={setSelectedProtectedHousingDocs}
            usedFor="register"
            companyLogo={companyLogo}
            landlordsDataFromShareProfile={landlordsData}
            disableEmail={isInvitedEmail}
          />
        )}
      </div>
    );
  }

  return (
    <div className="bigform tenant-auth-email auth-flat">
      <AuthSidePanel
        role={getUserRoleFromPath(window.location.pathname)}
        step={2}
        caption={_t("stepTwoOfThree")}
      />
      <div className="form-right new-form-right overflow-y-scroll">
        <div
          className={`logoWrapper tenant-auth-logo-wrapper ${
            haveValue(companyLogo) ? "withCompanyLogo" : ""
          }`}
        >
          {haveValue(companyLogo) && (
            <div className="form_logo">
              <img className="company-logo" src={`${companyLogo}`} alt="" />
              <div className="d-flex align-items-center justify-content-center custom-gap-2 poweredByWrapper">
                <p className="poweredBy">{_t("poweredBy")}</p>
                <img
                  src={Logo}
                  alt="Powered By Logo"
                  className="poweredByLogo"
                />
              </div>
            </div>
          )}

          <LanguageSwitch
            currentLang={currentLang}
            onChange={handleLanguageChange}
          />
        </div>
        <div className="form-inner">
          {currentScreen === "email" && (
            <EmailForm
              email={userLogin?.email ?? ""}
              onChangeHandler={onChangeHandler}
              disabled={disabled}
              loading={emailLoading}
              emailSubmitHandler={emailSubmitHandler}
              companyLogo={companyLogo}
              usedFor="tenant"
              from={from}
              isInvitedEmail={isInvitedEmail}
            />
          )}
          {currentScreen === "verifyOtp" && (
            <EnterOtpModal
              inline
              onClose={() => setCurrentScreen("email")}
              email={userLogin?.email ?? ""}
              verifyOTPHandler={(e) => submitHandler(e, true)}
              otp={otp}
              setOtp={setOtp}
              showResend={showResend}
              showTimer={showTimer}
              sendOtpFun={sendOtpFun}
              verifyOtpLoading={verifyOtpLoading}
            />
          )}

          <p className="cookiesdiv">
            {_t(
              "byClickingNextYouAcceptAllThe",
              "Text used in onboarding process"
            )}
            <Link to="https://skortorent.com/terms-conditions.html">
              {_t("termsAndConditions", "Text used in onboarding process")}
            </Link>
            <Link to="https://skortorent.com/privacy-policy.html">
              {_t("privacyPolicy", "Text used in onboarding process")}
            </Link>
            <Link to="https://skortorent.com/cookies.html">
              {_t("cookiePolicy", "Text used in onboarding process")}
            </Link>
            <Link to="https://skortorent.com/legal-notice.html">
              {_t("legalNotice", "Text used in onboarding process")}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TenantAuth;
